<template>
  <v-app id="inspire" class="ism-auth-app" light>
    <!-- Background Elements -->
    <div class="ism-auth-bg" :style="backgroundStyle"></div>
    
    <v-main class="ism-auth-main">
      <v-container fluid fill-height class="pa-0">
        <v-row align="center" justify="center" no-gutters style="height: 100vh;">
          <v-col cols="12" sm="10" md="8" lg="6" xl="4" class="d-flex justify-center">
            
            <!-- Auth Card -->
            <div class="ism-auth-card">
              <!-- Logo -->
              <div class="ism-auth-header">
                <img src="/LogoSvg.svg" alt="iSmartSociety" class="ism-auth-logo" />
                <h2 class="ism-auth-title">Reset Password</h2>
                <p class="ism-auth-subtitle">Enter your new password below</p>
              </div>

              <!-- Form -->
              <v-form @submit.prevent="resetPassword" class="ism-auth-form">
                <PremiumInput
                  v-model="user.password"
                  name="password"
                  label="New Password"
                  placeholder="Enter new password"
                  :type="showPassword ? 'text' : 'password'"
                  icon="mdi-lock-outline"
                  v-validate="'required'"
                  :error="errors.first('password') || serverSideErrors.password"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showPassword = !showPassword"
                />

                <PremiumInput
                  v-model="user.password_confirmation"
                  name="Confirm Password"
                  label="Confirm Password"
                  placeholder="Re-enter new password"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  icon="mdi-lock-check-outline"
                  v-validate="'required'"
                  :error="errors.first('Confirm Password') || serverSideErrors.password_confirmation"
                  :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="showConfirmPassword = !showConfirmPassword"
                />

                <!-- Submit -->
                <button type="submit" class="ism-auth-btn" :disabled="loading">
                  <v-progress-circular v-if="loading" indeterminate size="20" color="#fff" class="mr-2"></v-progress-circular>
                  <span v-else>Reset Password</span>
                </button>

                <!-- Back to Login -->
                <div class="ism-auth-footer">
                  <router-link to="/superadmin/login" class="ism-auth-link is-back">
                    <v-icon size="16" class="mr-1">mdi-arrow-left</v-icon> Back to Login
                  </router-link>
                </div>
              </v-form>
            </div>

          </v-col>
        </v-row>
      </v-container>
    </v-main>
    <Alert />
  </v-app>
</template>

<script>
import Alert from "../../Alert.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import "@/assets/style.css";
import { mapMutations } from "vuex";

export default {
  components: { Alert, PremiumInput },
  data() {
    return {
      showPassword: false,
      showConfirmPassword: false,
      user: {
        password: "",
        password_confirmation: "",
      },
      loading: false,
      token: '',
      serverSideErrors: {}
    };
  },
  computed: {
    backgroundStyle() {
      return {
        backgroundImage: `url(${process.env.VUE_APP_API_URL}/web_front/bulding.svg)`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    }
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    resetPassword() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.loading = true;
          this.$axios.post("/superadmin/resetPassword/" + this.token, this.user).then((response) => {
            const data = response.data;
            this.loading = false;
            this.serverSideErrors = {};
            if (data.status) {
              this.setAlert({ showAlert: true, variant: "green accent-4", timeout: 2000, message: data.message });
              this.$router.push({ path: "/superadmin/login" });
            } else {
              if (data.errors) {
                data.errors.forEach(e => { this.serverSideErrors[e.field] = e.message; });
              } else {
                this.setAlert({ showAlert: true, variant: "red accent-3", timeout: 2000, message: data.message });
              }
            }
          });
        }
      });
    },
  },
  mounted() {
    this.token = this.$route.params.token;
  }
};
</script>

<style scoped>
/* ── Force light mode on entire auth page ── */
.ism-auth-app {
  background-color: #f8fafc !important;
}

/* Override dark-mode CSS vars inside auth card */
.ism-auth-card {
  --card-bg:        #ffffff !important;
  --bg-primary:     #f1f5f9 !important;
  --text-primary:   #1e293b !important;
  --text-secondary: #64748b !important;
  --border-color:   rgba(0,0,0,0.09) !important;
  --border-color2:  rgba(0,0,0,0.15) !important;
}

/* Force PremiumInput to always render in light mode inside auth card */
.ism-auth-card ::v-deep .premium-label {
  color: #475569 !important;
}
.ism-auth-card ::v-deep .premium-input .v-input__slot {
  background: #f1f5f9 !important;
}
.ism-auth-card ::v-deep .premium-input fieldset {
  border-color: rgba(0, 0, 0, 0.12) !important;
}
.ism-auth-card ::v-deep .premium-input input {
  color: #1e293b !important;
}
.ism-auth-card ::v-deep .premium-input input::placeholder {
  color: #94a3b8 !important;
  opacity: 1 !important;
}
.ism-auth-card ::v-deep .premium-input .v-icon {
  color: #94a3b8 !important;
}
.ism-auth-card ::v-deep .premium-input .v-label {
  color: #64748b !important;
}

.ism-auth-bg {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 0;
}

.ism-auth-main {
  position: relative;
  z-index: 2;
}

.ism-auth-card {
  width: 100%;
  max-width: 440px;
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0,0,0,0.05);
  padding: 48px 40px;
  border: 1px solid rgba(0,0,0,0.05);
}

.ism-auth-header {
  text-align: center;
  margin-bottom: 32px;
}

.ism-auth-logo {
  height: 48px;
  max-width: 100%;
  margin-bottom: 24px;
  object-fit: contain;
}

.ism-auth-title {
  font-family: 'Inter', sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 8px 0;
  letter-spacing: -0.03em;
}

.ism-auth-subtitle {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.ism-auth-form {
  display: flex;
  flex-direction: column;
}

.ism-auth-btn {
  width: 100%;
  height: 48px;
  background: linear-gradient(135deg, #F26522, #C94F10);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(242, 101, 34, 0.3);
  transition: all 0.2s;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 8px;
}

.ism-auth-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(242, 101, 34, 0.4);
}

.ism-auth-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.ism-auth-footer {
  text-align: center;
  margin-top: 12px;
}
.ism-auth-link.is-back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-family: 'Inter', sans-serif;
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s;
}
.ism-auth-link.is-back:hover {
  color: #1e293b;
}

@media (max-width: 600px) {
  .ism-auth-card {
    padding: 40px 24px;
    border-radius: 20px;
    margin: 16px;
  }
}
</style>


<script>
import Alert from "../../Alert.vue";
import "@/assets/style.css";
import { mapMutations } from "vuex";

export default {
  components: {
    Alert,
  },
  data() {
    return {
      showPassword: false,
      showConfirmPassword: false,
      user: {
        password: "",
        password_confirmation: "",
      },
      loading: false,
      token: '',
      serverSideErrors: {}
    };
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    resetPassword() {
      let _self = this;

      this.$validator.validateAll().then((result) => {
        if (result) {
          _self.loading = true;

          _self.$axios.post("/superadmin/resetPassword/"+this.token, this.user).then((response) => {
            let data = response.data;
            _self.loading = false;
            _self.serverSideErrors = {};

            if (data.status) {
              _self.setAlert({
                showAlert: true,
                variant: "green accent-4",
                timeout: "2000",
                message: data.message,
              })
              this.$router.push({ path: "/superadmin/login" });
            } else {
              if (data.errors) {
                for (let i = 0; i < data.errors.length; i++) {
                  _self.serverSideErrors[data.errors[i].field] = data.errors[i].message;
                }
              } else {
                this.setAlert({
                  showAlert: true,
                  variant: "red accent-3",
                  timeout: "2000",
                  message: data.message,
                });
              }
            }
          });
        }
      });
    },
  },
  mounted() {
    this.token = this.$route.params.token;
  }
};
</script>

<style scoped>
.background-svg {
  background: url("http://127.0.0.1:3333/web_front/icon_bulding.svg") !important;
  background-size: 100% 100% !important;
}

.logo-svg {
  width: 150px !important;
}
</style>